/**
 * ============================================
 * STRAP FINDER WIDGET — DELUGS-STYLE
 * 4-step wizard: Brand → Model → Lug Width → Results
 * With Shopify AJAX cart integration
 * ============================================
 */
(function() {
  'use strict';

  class StrapFinderEngine {
    constructor(container) {
      this.container = container;
      this.currentStep = 1;
      this.selectedBrand = null;
      this.selectedModel = null;
      this.selectedLugWidth = null;
      this.activeTab = 'all';
      this.activeColour = 'all';
      this.database = {};
      this.products = [];
      this.filteredProducts = [];
      this.resultsUrl = (container.dataset.sfeResultsUrl || '/pages/strap-finder-results').trim();

      this.init();
    }

    init() {
      try {
        const dbEl = this.container.querySelector('[data-sfe-database]');
        const prodEl = this.container.querySelector('[data-sfe-products]');
        if (dbEl) this.database = JSON.parse(dbEl.textContent);
        if (prodEl) this.products = JSON.parse(prodEl.textContent);
      } catch (e) {
        console.error('[StrapFinder] Data parse error:', e);
      }

      const params = new URLSearchParams(window.location.search);
      const urlBrand = params.get('brand');
      const urlModel = params.get('model');
      const urlLug = params.get('lug');

      this.renderBrands();

      if (urlBrand && this.database[urlBrand]) {
        this.selectBrand(urlBrand, false);
        if (urlModel && this.database[urlBrand][urlModel]) {
          this.selectModel(urlModel, false);
          if (urlLug) {
            this.selectLugWidth(urlLug, false);
          }
        }
      }
    }

    // ==========================================
    //  STEP 1: BRAND
    // ==========================================
    renderBrands() {
      const grid = this.container.querySelector('[data-sfe-brand-grid]');
      if (!grid) return;

      const brands = Object.keys(this.database).sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
      );

      if (brands.length === 0) {
        grid.innerHTML = this.emptyState('No brands available', 'Add straps with compatibility metafields');
        return;
      }

      grid.innerHTML = brands.map((brand, i) => {
        const modelCount = Object.keys(this.database[brand]).length;
        const totalStraps = this.getStrapCountForBrand(brand);
        const initial = brand.charAt(0).toUpperCase();
        return `
          <div class="sfe-brand-tile" data-brand="${this.escapeHtml(brand)}" style="animation-delay: ${i * 0.05}s" tabindex="0" role="button" aria-label="Select ${this.escapeHtml(brand)}">
            <div class="sfe-brand-tile__logo">${initial}</div>
            <div class="sfe-brand-tile__name">${this.escapeHtml(brand)}</div>
            <div class="sfe-brand-tile__count">${totalStraps} strap${totalStraps !== 1 ? 's' : ''} · ${modelCount} model${modelCount !== 1 ? 's' : ''}</div>
          </div>`;
      }).join('');

      grid.querySelectorAll('.sfe-brand-tile').forEach(tile => {
        tile.addEventListener('click', () => this.selectBrand(tile.dataset.brand));
        tile.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectBrand(tile.dataset.brand);
          }
        });
      });
    }

    selectBrand(brand, updateUrl = true) {
      this.selectedBrand = brand;
      this.selectedModel = null;
      this.selectedLugWidth = null;
      this.goToStep(2);
      this.renderModels(brand);
      if (updateUrl) this.updateUrl();
    }

    // ==========================================
    //  STEP 2: MODEL
    // ==========================================
    renderModels(brand) {
      const grid = this.container.querySelector('[data-sfe-model-grid]');
      const headerEl = this.container.querySelector('[data-sfe-model-header]');
      if (!grid) return;

      if (headerEl) {
        headerEl.innerHTML = `
          <div class="sfe-step__title">Select your <em>${this.escapeHtml(brand)}</em> model</div>
          <div class="sfe-step__subtitle">Choose the specific watch model for perfect strap compatibility</div>`;
      }

      const models = this.database[brand];
      if (!models) return;

      const modelNames = Object.keys(models).sort((a, b) => {
        const countA = this.getStrapCountForModel(brand, a);
        const countB = this.getStrapCountForModel(brand, b);
        return countB - countA;
      });

      grid.innerHTML = modelNames.map(model => {
        const data = models[model];
        const strapCount = this.getStrapCountForModel(brand, model);
        const lugs = Object.keys(data.lug_widths || {}).sort((a, b) => parseInt(a) - parseInt(b));
        const lugText = lugs.length > 0 ? lugs.map(l => l + 'mm').join(', ') : 'N/A';

        return `
          <div class="sfe-model-card" data-model="${this.escapeHtml(model)}" tabindex="0" role="button" aria-label="Select ${this.escapeHtml(model)}">
            <div class="sfe-model-card__name">${this.escapeHtml(model)}</div>
            <div class="sfe-model-card__meta">
              <span class="sfe-model-card__badge sfe-model-card__badge--straps">${strapCount} strap${strapCount !== 1 ? 's' : ''}</span>
              <span class="sfe-model-card__badge sfe-model-card__badge--lug">Lug: ${lugText}</span>
            </div>
          </div>`;
      }).join('');

      const searchInput = this.container.querySelector('[data-sfe-model-search]');
      if (searchInput) {
        searchInput.value = '';
        searchInput.oninput = () => {
          const q = searchInput.value.toLowerCase().trim();
          grid.querySelectorAll('.sfe-model-card').forEach(card => {
            const name = card.dataset.model.toLowerCase();
            card.style.display = name.includes(q) ? '' : 'none';
          });
        };
      }

      grid.querySelectorAll('.sfe-model-card').forEach(card => {
        card.addEventListener('click', () => this.selectModel(card.dataset.model));
        card.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectModel(card.dataset.model);
          }
        });
      });
    }

    selectModel(model, updateUrl = true) {
      this.selectedModel = model;
      this.selectedLugWidth = null;

      const modelData = this.database[this.selectedBrand]?.[model];
      const lugs = Object.keys(modelData?.lug_widths || {});

      if (lugs.length === 1) {
        this.selectLugWidth(lugs[0], updateUrl);
      } else {
        this.goToStep(3);
        this.renderLugWidths(lugs);
        if (updateUrl) this.updateUrl();
      }
    }

    // ==========================================
    //  STEP 3: LUG WIDTH
    // ==========================================
    renderLugWidths(lugs) {
      const grid = this.container.querySelector('[data-sfe-lug-grid]');
      const headerEl = this.container.querySelector('[data-sfe-lug-header]');
      if (!grid) return;

      if (headerEl) {
        headerEl.innerHTML = `
          <div class="sfe-step__title">Select <em>lug width</em></div>
          <div class="sfe-step__subtitle">${this.escapeHtml(this.selectedBrand)} ${this.escapeHtml(this.selectedModel)} is available in multiple sizes</div>`;
      }

      grid.innerHTML = lugs.sort((a, b) => parseInt(a) - parseInt(b)).map(lug => {
        const count = this.database[this.selectedBrand][this.selectedModel].lug_widths[lug];
        return `
          <div class="sfe-lug-card" data-lug="${this.escapeHtml(lug)}" tabindex="0" role="button" aria-label="Select ${lug}mm lug width">
            <div class="sfe-lug-card__size">${this.escapeHtml(lug)}</div>
            <div class="sfe-lug-card__unit">mm</div>
            <div class="sfe-lug-card__count">${count} strap${count !== 1 ? 's' : ''}</div>
          </div>`;
      }).join('');

      grid.querySelectorAll('.sfe-lug-card').forEach(card => {
        card.addEventListener('click', () => this.selectLugWidth(card.dataset.lug));
        card.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectLugWidth(card.dataset.lug);
          }
        });
      });
    }

    selectLugWidth(lug, updateUrl = true) {
      this.selectedLugWidth = lug;
      this.goToStep(4);
      this.renderResults();
      if (updateUrl) this.updateUrl();
    }

    // ==========================================
    //  STEP 4: RESULTS
    // ==========================================
    renderResults() {
      const brand = this.selectedBrand;
      const model = this.selectedModel;
      const lug = this.selectedLugWidth;
      if (!brand || !model) return;

      const lugText = lug ? lug + 'mm' : 'Various';
      const strapCount = this.getStrapCountForModel(brand, model, lug);

      const hero = this.container.querySelector('[data-sfe-results-hero]');
      if (hero) {
        hero.innerHTML = `
          <div class="sfe-results-hero__brand">${this.escapeHtml(brand)} · ${this.escapeHtml(model)}${lug ? ' · ' + lug + 'mm' : ''}</div>
          <h2 class="sfe-results-hero__title">Compatible straps for your <strong>${this.escapeHtml(brand)} ${this.escapeHtml(model)}</strong></h2>
          <div class="sfe-results-hero__specs">
            <div class="sfe-results-hero__spec">
              <span class="sfe-results-hero__spec-label">Lug Width</span>
              <span class="sfe-results-hero__spec-value">${lugText}</span>
            </div>
            <div class="sfe-results-hero__spec-divider"></div>
            <div class="sfe-results-hero__spec">
              <span class="sfe-results-hero__spec-label">Available</span>
              <span class="sfe-results-hero__spec-value">${strapCount} straps</span>
            </div>
            <div class="sfe-results-hero__spec-divider"></div>
            <div class="sfe-results-hero__spec">
              <span class="sfe-results-hero__spec-label">Materials</span>
              <span class="sfe-results-hero__spec-value">${this.getMaterialsForModel(brand, model, lug)}</span>
            </div>
          </div>
          <div class="sfe-results-hero__cta">
            <button type="button" class="sfe-btn-tryon" data-sfe-tryon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 2"/></svg>
              Try these on your ${this.escapeHtml(brand)} ${this.escapeHtml(model)}
            </button>
          </div>`;

        const tryOnBtn = hero.querySelector('[data-sfe-tryon]');
        if (tryOnBtn) {
          tryOnBtn.addEventListener('click', () => this.goToTryOn());
        }
      }

      this.filteredProducts = this.products.filter(p => {
        const brands = Array.isArray(p.brands) ? p.brands : [p.brands];
        const models = Array.isArray(p.models) ? p.models : [p.models];
        const brandMatch = brands.some(b => b && b.toLowerCase() === brand.toLowerCase());
        const modelMatch = models.some(m => m && m.toLowerCase() === model.toLowerCase());
        const lugMatch = !lug || String(p.lug_width).trim() === String(lug).trim();
        return brandMatch && modelMatch && lugMatch;
      });

      this.buildFilters();
      this.renderTabs();
      this.renderColours();
      this.renderProducts();
    }

    buildFilters() {
      this.materialCounts = { all: this.filteredProducts.length };
      this.colourCounts = { all: this.filteredProducts.length };

      this.filteredProducts.forEach(p => {
        const mat = (p.material || 'other').toLowerCase();
        this.materialCounts[mat] = (this.materialCounts[mat] || 0) + 1;

        const col = (p.color || 'other').toLowerCase();
        this.colourCounts[col] = (this.colourCounts[col] || 0) + 1;
      });
    }

    renderTabs() {
      const tabsEl = this.container.querySelector('[data-sfe-tabs]');
      if (!tabsEl) return;

      const matOrder = ['all', 'leather', 'rubber', 'fabric', 'metal', 'canvas', 'nylon'];
      const materials = Object.keys(this.materialCounts).sort((a, b) => {
        const ia = matOrder.indexOf(a);
        const ib = matOrder.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });

      tabsEl.innerHTML = materials.map(mat => {
        const count = this.materialCounts[mat];
        const label = mat === 'all' ? 'All' : mat.charAt(0).toUpperCase() + mat.slice(1);
        const active = this.activeTab === mat ? 'sfe-tab--active' : '';
        return `<button class="sfe-tab ${active}" data-tab="${mat}">${label}<span class="sfe-tab__count">(${count})</span></button>`;
      }).join('');

      tabsEl.querySelectorAll('.sfe-tab').forEach(btn => {
        btn.addEventListener('click', () => {
          this.activeTab = btn.dataset.tab;
          this.renderTabs();
          this.renderColours();
          this.renderProducts();
        });
      });
    }

    renderColours() {
      const coloursEl = this.container.querySelector('[data-sfe-colours]');
      if (!coloursEl) return;

      const colourMap = { all: 0 };
      this.getVisibleProducts().forEach(p => {
        const col = (p.color || '').toLowerCase();
        if (col) colourMap[col] = (colourMap[col] || 0) + 1;
        colourMap.all++;
      });

      const swatchColors = {
        black: '#1a1a1a', brown: '#6b3a2a', blue: '#1e40af', green: '#15803d',
        red: '#b91c1c', navy: '#172554', grey: '#6b7280', gray: '#6b7280',
        white: '#f5f5f5', orange: '#ea580c', tan: '#c2956b', natural: '#d4b896',
        taupe: '#8B7D6B', beige: '#C8B898', yellow: '#eab308', silver: '#C0C0C0'
      };

      // Map each colour name → an explicit hex (from product strap.color_hex) when available
      const colourHexByName = {};
      this.getVisibleProducts().forEach(p => {
        const col = (p.color || '').toLowerCase();
        if (col && p.color_hex && !colourHexByName[col]) colourHexByName[col] = p.color_hex;
      });

      const colours = Object.keys(colourMap);
      if (colours.length <= 1) {
        coloursEl.innerHTML = '';
        return;
      }

      coloursEl.innerHTML = colours.map(col => {
        const label = col === 'all' ? 'All' : col.charAt(0).toUpperCase() + col.slice(1);
        const active = this.activeColour === col ? 'sfe-colour-pill--active' : '';
        const hex = col !== 'all' ? (colourHexByName[col] || swatchColors[col]) : null;
        const swatch = hex
          ? `<span class="sfe-colour-pill__swatch" style="background:${hex}"></span>`
          : '';
        return `<button class="sfe-colour-pill ${active}" data-colour="${col}">${swatch}${label}</button>`;
      }).join('');

      coloursEl.querySelectorAll('.sfe-colour-pill').forEach(btn => {
        btn.addEventListener('click', () => {
          this.activeColour = btn.dataset.colour;
          this.renderColours();
          this.renderProducts();
        });
      });
    }

    getVisibleProducts() {
      let prods = this.filteredProducts;
      if (this.activeTab !== 'all') {
        prods = prods.filter(p => (p.material || '').toLowerCase() === this.activeTab);
      }
      return prods;
    }

    renderProducts() {
      const gridEl = this.container.querySelector('[data-sfe-products-grid]');
      const countEl = this.container.querySelector('[data-sfe-results-count]');
      if (!gridEl) return;

      let prods = this.getVisibleProducts();

      if (this.activeColour !== 'all') {
        prods = prods.filter(p => (p.color || '').toLowerCase() === this.activeColour);
      }

      if (countEl) {
        countEl.innerHTML = `Showing <strong>${prods.length}</strong> strap${prods.length !== 1 ? 's' : ''}`;
      }

      if (prods.length === 0) {
        gridEl.innerHTML = this.emptyState('No straps match your filters', 'Try changing the material or colour filter');
        return;
      }

      gridEl.innerHTML = prods.map(p => {
        const imgHtml = p.image
          ? `<img src="${p.image}" alt="${this.escapeHtml(p.title)}" loading="lazy" width="400" height="400" class="sfe-product-card__img-primary">`
          : `<div class="sfe-product-card__image-placeholder">No image</div>`;

        const imgSecondary = p.images && p.images[1]
          ? `<img src="${p.images[1]}" alt="${this.escapeHtml(p.title)}" loading="lazy" width="400" height="400" class="sfe-product-card__img-secondary">`
          : '';

        const materialBadge = p.material
          ? `<span class="sfe-product-card__badge sfe-product-card__badge--material">${this.escapeHtml(p.material)}</span>`
          : '';

        const priceHtml = this.formatPrice(p.price);
        const compareHtml = p.compare_price && p.compare_price > p.price
          ? `<span class="sfe-product-card__price-compare">${this.formatPrice(p.compare_price)}</span>`
          : '';

        // Variant selector if multiple variants
        let variantSelect = '';
        if (p.variants && p.variants.length > 1) {
          const options = p.variants.map(v =>
            `<option value="${v.id}" ${!v.available ? 'disabled' : ''}>${this.escapeHtml(v.title)}${!v.available ? ' (Sold out)' : ''}</option>`
          ).join('');
          variantSelect = `<select class="sfe-variant-select" data-variant-select="${p.id}">${options}</select>`;
        }

        const defaultVariant = p.variants && p.variants.find(v => v.available) || p.variants[0];
        const variantId = defaultVariant ? defaultVariant.id : '';
        const canAdd = defaultVariant && defaultVariant.available;

        return `
          <div class="sfe-product-card" data-product-id="${p.id}">
            <a href="${p.url}" class="sfe-product-card__image" style="display:block;text-decoration:none;">
              ${imgHtml}
              ${imgSecondary}
              ${materialBadge}
            </a>
            <div class="sfe-product-card__info">
              <div class="sfe-product-card__title"><a href="${p.url}" style="color:inherit;text-decoration:none;">${this.escapeHtml(p.title)}</a></div>
              <div class="sfe-product-card__price">${priceHtml}${compareHtml}</div>
            </div>
            ${variantSelect}
            <div class="sfe-product-card__actions">
              <button class="sfe-btn-tryon-sm" data-tryon-ref="${this.escapeHtml(p.handle || '')}" data-tryon-image="${this.escapeHtml(p.image || '')}" type="button" aria-label="Try ${this.escapeHtml(p.title)} on your watch">
                Try on
              </button>
              <button class="sfe-btn-atc" data-add-to-cart="${variantId}" data-product-title="${this.escapeHtml(p.title)}" ${!canAdd ? 'disabled' : ''}>
                ${canAdd ? 'Add to Cart' : 'Sold Out'}
              </button>
            </div>
          </div>`;
      }).join('');

      // Bind per-card "Try on" buttons → hand off to the try-on results page
      gridEl.querySelectorAll('[data-tryon-ref]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.goToTryOn({ ref: btn.dataset.tryonRef, image: btn.dataset.tryonImage });
        });
      });

      // Bind add to cart buttons
      gridEl.querySelectorAll('[data-add-to-cart]').forEach(btn => {
        if (btn.disabled) return;
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const variantId = btn.dataset.addToCart;
          const title = btn.dataset.productTitle;
          const card = btn.closest('.sfe-product-card');
          const select = card?.querySelector('[data-variant-select]');
          const selectedVariantId = select ? select.value : variantId;
          this.addToCart(selectedVariantId, title, btn);
        });
      });
    }

    // ==========================================
    //  CART INTEGRATION
    // ==========================================
    async addToCart(variantId, title, btn) {
      if (!variantId) return;

      const originalText = btn.innerHTML;
      btn.innerHTML = '<span class="sfe-btn-atc__spinner"></span>';
      btn.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        if (response.ok) {
          if (window.sfTrack) {
            window.sfTrack('strap_finder_add_to_cart', {
              variant_id: variantId,
              product_title: title,
              brand: this.selectedBrand,
              model: this.selectedModel,
              lug_width: this.selectedLugWidth
            });
          }
          this.showToast(title);
          // Update cart count if element exists
          const cartCount = document.querySelector('.thb-item-count, .cart-count, [data-cart-count]');
          if (cartCount) {
            const cartRes = await fetch('/cart.js');
            const cart = await cartRes.json();
            cartCount.textContent = cart.item_count;
          }
        } else {
          const err = await response.json();
          alert(err.description || 'Could not add to cart');
        }
      } catch (e) {
        console.error('Add to cart error:', e);
        alert('Network error. Please try again.');
      } finally {
        btn.innerHTML = originalText;
        btn.disabled = false;
      }
    }

    showToast(title) {
      const toast = this.container.querySelector('[data-sfe-toast]');
      const productEl = this.container.querySelector('[data-sfe-toast-product]');
      if (!toast) return;

      if (productEl) productEl.textContent = title;
      toast.classList.add('is-visible');

      setTimeout(() => {
        toast.classList.remove('is-visible');
      }, 4000);
    }

    // ==========================================
    //  STEP NAVIGATION
    // ==========================================
    goToStep(step) {
      this.currentStep = step;

      // Funnel instrumentation — every wizard step transition passes through here.
      if (window.sfTrack) {
        var stepNames = { 1: 'brand', 2: 'model', 3: 'lug_width', 4: 'results' };
        window.sfTrack('strap_finder_step', {
          step: step,
          step_name: stepNames[step] || String(step),
          brand: this.selectedBrand,
          model: this.selectedModel,
          lug_width: this.selectedLugWidth
        });
      }

      this.container.querySelectorAll('[data-sfe-progress-dot]').forEach(dot => {
        const s = parseInt(dot.dataset.sfeProgressDot);
        dot.classList.remove('sfe-progress__dot--active', 'sfe-progress__dot--completed');
        if (s === step) dot.classList.add('sfe-progress__dot--active');
        else if (s < step) dot.classList.add('sfe-progress__dot--completed');
      });

      this.container.querySelectorAll('[data-sfe-progress-line]').forEach(line => {
        const s = parseInt(line.dataset.sfeProgressLine);
        line.classList.toggle('sfe-progress__line--active', s < step);
      });

      this.container.querySelectorAll('[data-sfe-step]').forEach(stepEl => {
        const s = parseInt(stepEl.dataset.sfeStep);
        stepEl.classList.toggle('sfe-step--active', s === step);
      });

      this.renderBreadcrumb();
      this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    goBack() {
      if (this.currentStep === 2) {
        this.selectedBrand = null;
        this.selectedModel = null;
        this.selectedLugWidth = null;
        this.goToStep(1);
      } else if (this.currentStep === 3) {
        this.selectedLugWidth = null;
        this.goToStep(2);
        this.renderModels(this.selectedBrand);
      } else if (this.currentStep === 4) {
        const modelData = this.database[this.selectedBrand]?.[this.selectedModel];
        const lugs = Object.keys(modelData?.lug_widths || {});
        if (lugs.length === 1) {
          this.selectedModel = null;
          this.selectedLugWidth = null;
          this.goToStep(2);
          this.renderModels(this.selectedBrand);
        } else {
          this.selectedLugWidth = null;
          this.goToStep(3);
          this.renderLugWidths(lugs);
        }
      }
      this.updateUrl();
    }

    renderBreadcrumb() {
      const bcEl = this.container.querySelector('[data-sfe-breadcrumb]');
      if (!bcEl) return;

      let html = '<span class="sfe-breadcrumb__link" data-sfe-bc-step="1">Strap Finder</span>';

      if (this.currentStep >= 2 && this.selectedBrand) {
        html += '<span class="sfe-breadcrumb__sep">›</span>';
        if (this.currentStep === 2) {
          html += `<span class="sfe-breadcrumb__current">${this.escapeHtml(this.selectedBrand)}</span>`;
        } else {
          html += `<span class="sfe-breadcrumb__link" data-sfe-bc-step="2">${this.escapeHtml(this.selectedBrand)}</span>`;
        }
      }

      if (this.currentStep >= 3 && this.selectedModel) {
        html += '<span class="sfe-breadcrumb__sep">›</span>';
        if (this.currentStep === 3) {
          html += `<span class="sfe-breadcrumb__current">${this.escapeHtml(this.selectedModel)}</span>`;
        } else {
          html += `<span class="sfe-breadcrumb__link" data-sfe-bc-step="3">${this.escapeHtml(this.selectedModel)}</span>`;
        }
      }

      if (this.currentStep >= 4 && this.selectedLugWidth) {
        html += '<span class="sfe-breadcrumb__sep">›</span>';
        html += `<span class="sfe-breadcrumb__current">${this.escapeHtml(this.selectedLugWidth)}mm</span>`;
      }

      bcEl.innerHTML = html;

      bcEl.querySelectorAll('[data-sfe-bc-step]').forEach(link => {
        link.addEventListener('click', () => {
          const target = parseInt(link.dataset.sfeBcStep);
          if (target === 1) {
            this.selectedBrand = null;
            this.selectedModel = null;
            this.selectedLugWidth = null;
            this.goToStep(1);
          } else if (target === 2) {
            this.selectedModel = null;
            this.selectedLugWidth = null;
            this.goToStep(2);
            this.renderModels(this.selectedBrand);
          } else if (target === 3) {
            this.selectedLugWidth = null;
            this.goToStep(3);
            const lugs = Object.keys(this.database[this.selectedBrand]?.[this.selectedModel]?.lug_widths || {});
            this.renderLugWidths(lugs);
          }
          this.updateUrl();
        });
      });
    }

    // ==========================================
    //  HELPERS
    // ==========================================
    getStrapCountForBrand(brand) {
      let count = 0;
      const models = this.database[brand] || {};
      Object.values(models).forEach(m => {
        Object.values(m.lug_widths || {}).forEach(c => count += c);
      });
      return count;
    }

    getStrapCountForModel(brand, model, lug) {
      let count = 0;
      const data = this.database[brand]?.[model];
      if (data) {
        if (lug) {
          count = data.lug_widths[lug] || 0;
        } else {
          Object.values(data.lug_widths || {}).forEach(c => count += c);
        }
      }
      return count;
    }

    getMaterialsForModel(brand, model, lug) {
      const materials = new Set();
      this.products.forEach(p => {
        const brands = Array.isArray(p.brands) ? p.brands : [p.brands];
        const models = Array.isArray(p.models) ? p.models : [p.models];
        const brandMatch = brands.some(b => b && b.toLowerCase() === brand.toLowerCase());
        const modelMatch = models.some(m => m && m.toLowerCase() === model.toLowerCase());
        const lugMatch = !lug || String(p.lug_width).trim() === String(lug).trim();
        if (brandMatch && modelMatch && lugMatch && p.material) {
          materials.add(p.material);
        }
      });
      const arr = Array.from(materials);
      return arr.length > 0 ? arr.join(', ') : 'Various';
    }

    formatPrice(cents) {
      if (!cents) return '';
      const currency = window.Shopify?.currency?.active || 'GBP';
      const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency + ' ';
      return symbol + (cents / 100).toFixed(2);
    }

    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    emptyState(title, subtitle) {
      return `
        <div class="sfe-empty" style="grid-column: 1/-1;">
          <div class="sfe-empty__icon">&#128269;</div>
          <div class="sfe-empty__text">${this.escapeHtml(title)}</div>
          <div class="sfe-empty__subtext">${this.escapeHtml(subtitle)}</div>
        </div>`;
    }

    // Build the handoff URL into the try-on / configurator results page.
    // Flow B (strap-finder-tryon + StrapFinderState) keys entirely off these params.
    buildTryOnUrl(extra) {
      const params = new URLSearchParams();
      if (this.selectedBrand) params.set('brand', this.selectedBrand);
      if (this.selectedModel) params.set('model', this.selectedModel);
      if (this.selectedLugWidth) params.set('lug', this.selectedLugWidth);
      if (extra) {
        if (extra.ref) params.set('ref', extra.ref);
        if (extra.image) params.set('image', extra.image);
      }
      const base = this.resultsUrl || '/pages/strap-finder-results';
      return base + (params.toString() ? '?' + params.toString() : '');
    }

    goToTryOn(extra) {
      window.location.href = this.buildTryOnUrl(extra);
    }

    updateUrl() {
      const params = new URLSearchParams();
      if (this.selectedBrand) params.set('brand', this.selectedBrand);
      if (this.selectedModel) params.set('model', this.selectedModel);
      if (this.selectedLugWidth) params.set('lug', this.selectedLugWidth);
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      history.replaceState(null, '', newUrl);
    }
  }

  // ==========================================
  //  AUTO-INITIALIZE
  // ==========================================
  function initEngines() {
    document.querySelectorAll('[data-strap-finder-engine]').forEach(el => {
      if (!el._sfeInstance) {
        el._sfeInstance = new StrapFinderEngine(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEngines);
  } else {
    initEngines();
  }

  document.addEventListener('shopify:section:load', function(e) {
    const container = e.target.querySelector('[data-strap-finder-engine]');
    if (container) {
      container._sfeInstance = null;
      initEngines();
    }
  });
})();
